import { useSyncExternalStore, useCallback } from 'react';
import { prefsStore } from '@lib/prefsStore';

interface UseSidebarResult {
  /** Whether the rail is collapsed to icon-only. */
  collapsed: boolean;
  /** Toggle the rail between full-width and icon-only. */
  toggleCollapsed: () => void;
  /** Whether a labeled nav section is collapsed (hidden items). */
  isSectionCollapsed: (key: string) => boolean;
  /** Collapse/expand a labeled nav section by its heading key. */
  toggleSection: (key: string) => void;
}

/**
 * Reactive accessor for sidebar UI preferences (rail collapse + per-section
 * collapse). Backed by prefsStore (localStorage-persisted), so the rail and
 * submenu states survive a reload — mirrors `useTheme`.
 */
export function useSidebar(): UseSidebarResult {
  const prefs = useSyncExternalStore(
    (listener) => prefsStore.subscribe(listener),
    () => prefsStore.get(),
    () => prefsStore.get(),
  );

  const toggleCollapsed = useCallback(() => prefsStore.toggleSidebar(), []);
  const toggleSection = useCallback((key: string) => prefsStore.toggleNavSection(key), []);
  const isSectionCollapsed = useCallback(
    (key: string) => prefs.collapsedNavSections.includes(key),
    [prefs.collapsedNavSections],
  );

  return { collapsed: prefs.sidebarCollapsed, toggleCollapsed, isSectionCollapsed, toggleSection };
}
